import { AlertCircle, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  MoyasarPaymentForm,
  type MoyasarPaymentFormConfig,
} from '@/components/billing/MoyasarPaymentForm';
import { useMoyasarPaymentLifecycle } from '@/lib/billing/moyasarPaymentLifecycle';
import { cn } from '@/lib/utils';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import type { ReactNode } from 'react';
import { PricingBreakdown } from '@/components/billing/PricingBreakdown';
import { paymentFormMatchesPricingDisplay } from '@/lib/billing/pricingDisplay';
import { cancelUnsubmittedPayment } from '@/lib/billing/cancelUnsubmittedPayment';

interface SecurePaymentDialogProps {
  open: boolean;
  config: MoyasarPaymentFormConfig | null;
  client: SupabaseClient<Database>;
  brand: 'forkhire' | 'forkseek';
  title: string;
  pricingSummary?: ReactNode;
  onBack: () => void;
}

export function SecurePaymentDialog({
  open,
  config,
  client,
  brand,
  title,
  pricingSummary,
  onBack,
}: SecurePaymentDialogProps) {
  const { t } = useTranslation();
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(false);
  const {
    submissionLocked,
    isLockedNow,
    onLifecycleChange,
    reset: resetPaymentLifecycle,
  } = useMoyasarPaymentLifecycle();
  const accent = brand === 'forkseek' ? 'text-seek' : 'text-primary';
  const pricingMatches = config
    ? paymentFormMatchesPricingDisplay(config.pricing_display, config.amount)
    : false;
  useEffect(() => {
    resetPaymentLifecycle();
    setCancelError(false);
  }, [config?.transaction_id, resetPaymentLifecycle]);

  const requestClose = async () => {
    if (isCancelling || isLockedNow()) return;
    if (!config) {
      onBack();
      return;
    }
    setIsCancelling(true);
    setCancelError(false);
    try {
      await cancelUnsubmittedPayment(client, config.transaction_id);
      onBack();
    } catch (error) {
      console.error('[payment] governed checkout cancellation failed', error);
      setCancelError(true);
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) void requestClose(); }}>
      <DialogContent
        closeDisabled={submissionLocked || isCancelling}
        className="max-h-[92vh] max-w-xl overflow-y-auto sm:rounded-xl"
        onEscapeKeyDown={(event) => {
          if (submissionLocked || isCancelling) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (submissionLocked || isCancelling) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (submissionLocked || isCancelling) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className={cn('h-5 w-5', accent)} />
            {title}
          </DialogTitle>
          <DialogDescription>{t('hire.billingConfirmation.description')}</DialogDescription>
        </DialogHeader>

        {pricingSummary ?? (
          config?.pricing_display
            ? <PricingBreakdown pricing={config.pricing_display} />
            : null
        )}

        {config && pricingMatches ? (
          <MoyasarPaymentForm
            config={config}
            client={client}
            onLifecycleChange={onLifecycleChange}
          />
        ) : config ? (
          <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('pricingDisplay.pricingMismatch')}</span>
          </div>
        ) : null}

        {cancelError && (
          <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('pricingDisplay.paymentCancelFailed')}</span>
          </div>
        )}
        {submissionLocked && (
          <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm text-foreground">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            <span>{t('pricingDisplay.paymentSubmitting')}</span>
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          className="rounded-xl"
          disabled={isCancelling || submissionLocked}
          onClick={() => void requestClose()}
        >
          {isCancelling ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <ArrowLeft className="me-2 h-4 w-4 rtl-flip" />}
          {isCancelling ? t('pricingDisplay.cancellingPayment') : t('common.back')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
